import {Skeleton} from '@mantine/core';
import {MdArrowForward} from 'react-icons/md';
import {routes} from '../../../../config/constants';
import type {RakingListItemType} from '../../../../core/entities/company/schema';
import {CommonLink} from '../../../../shared/components/CommonLink';
import {LoginPromptOverlay} from '../../../../shared/components/LoginPromptOverlay';
import {CompanyCardFollowRanking} from '../../../company/components/Container/Presenter/CompanyCardFollowRanking';
import {SectionHeader} from './Presenter/SectionHeader';
import {CAROUSEL_CONFIG} from './constants';

type Props = {
  componentId: string;
  title: string;
  description?: string;
  isLoading: boolean;
  isLogin?: boolean;
  isStudentVerificationRequired?: boolean;
  rankingItems?: RakingListItemType[];
};

export const CompanyRankingSection = ({
  componentId,
  title,
  description,
  isLoading,
  isLogin,
  isStudentVerificationRequired = false,
  rankingItems,
}: Props) => {
  if (!isLoading && rankingItems?.length === 0) {
    return null;
  }

  // 大学認証未完了ユーザーは未ログインと同様にランキングを閲覧できない
  const isViewable = isLogin && !isStudentVerificationRequired;

  const ShowMoreCard = () => {
    return (
      <CommonLink
        href={`${routes.COMPANY_FOLLOW_RANKING}?term=daily`}
        className="shadow-card bg-white p-16 rounded-16 hover:opacity-70 pc:mt-16 flex items-center"
        data-gtm-click={`${componentId}-more`}
      >
        <div className="flex justify-center py-16">
          <MdArrowForward size={40} />
        </div>
        <div>
          <div className="text-md text-black text-center w-[200px]">
            もっと見る
          </div>
        </div>
      </CommonLink>
    );
  };

  return (
    <div id={componentId} className="w-full py-16">
      <div className="pc:w-[1200px]">
        <SectionHeader
          componentId={componentId}
          title={title}
          description={description}
          href={
            isViewable
              ? `${routes.COMPANY_FOLLOW_RANKING}?term=daily`
              : undefined
          }
        />

        {!isViewable ? (
          <LoginPromptOverlay
            title="フォロー企業ランキング"
            description="企業のフォローランキングをご覧いただくには会員登録が必要です。"
            studentVerificationDescription="企業のフォローランキングをご覧いただくには大学認証が必要です。"
            isStudentVerificationRequired={isStudentVerificationRequired}
          >
            <div className="flex mt-16 gap-16 pc:gap-24 overflow-x-scroll">
              {isLoading ? (
                <CompanyCardSkeletons />
              ) : (
                <CompanyCards
                  rankingItems={rankingItems ?? []}
                  componentId={componentId}
                />
              )}
            </div>
          </LoginPromptOverlay>
        ) : (
          <div className="flex mt-16 gap-16 pc:gap-24 overflow-x-scroll">
            {isLoading ? (
              <CompanyCardSkeletons />
            ) : (
              <CompanyCards
                rankingItems={rankingItems ?? []}
                componentId={componentId}
              />
            )}
            {rankingItems?.length === CAROUSEL_CONFIG.COMPANY_LIMIT && (
              <ShowMoreCard />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const CompanyCards = ({
  rankingItems,
  componentId,
}: {rankingItems: RakingListItemType[]; componentId: string}) => {
  return (
    <>
      {rankingItems.map((item) => (
        <div key={item.company.id} className="rounded-[16px] flex-shrink-0">
          <CompanyCardFollowRanking
            rankingItem={item}
            isAllTerm={false}
            componentId={`${componentId}-item`}
          />
        </div>
      ))}
    </>
  );
};

const CompanyCardSkeletons = () => {
  return Array.from({length: CAROUSEL_CONFIG.SKELETON_COUNT}).map(
    (_, index) => (
      <div key={`skeleton-company-${index}`}>
        <Skeleton
          height={CAROUSEL_CONFIG.CARD_HEIGHT.COMPANY}
          width={CAROUSEL_CONFIG.PC.COMPANY_CARD_WIDTH}
        />
        <Skeleton height={30} width={CAROUSEL_CONFIG.PC.COMPANY_CARD_WIDTH} />
        <Skeleton height={30} width={CAROUSEL_CONFIG.PC.COMPANY_CARD_WIDTH} />
      </div>
    ),
  );
};
